import { computed } from 'vue';
import { useAuth } from '/app/composables/auth';
import { usePreference } from '/app/composables/preferences';
import { useToast } from '/app/composables/toast';
import { useBrain } from '/app/queries/gamedash';
import { track } from '/app/utils/analytics';
import { NEWBIE_LEVEL_LIMIT, WIDGET_REASON } from '/app/constant/game-dash';

// Same key shape the GameDashProvider uses on `overall` routes, so the query
// cache is shared and most pages pay no extra request.
const OVERALL_PARAMS = { role: 'overall' };

/**
 * The user's "keep the Game Dash in the right column" preference. Global, not
 * per platform. Pinning only ever adds visibility - it never hides a widget
 * the newbie rule already shows.
 */
export const useGameDashPin = () => {
    const { model, ready: loaded, saving, save } = usePreference({ scope: 'user', path: 'gamedash.pinned', default: false });
    const toast = useToast();
    const pinned = computed(() => model.value === true);
    const ready = computed(() => loaded.value && !saving.value);

    // Resolves to the saved value, or null when nothing was saved.
    const toggle = async (props = {}) => {
        if (!ready.value) return null;

        const next = !pinned.value;

        // Optimistic: `save` sets the value first, so the sidebar widget shows
        // up behind the open dialog, and reverts it if the request fails.
        try {
            await save(next);

            track(next ? 'Game Dash Pinned' : 'Game Dash Unpinned', props);

            return next;
        } catch (e) {
            toast.error(next ? 'Could not pin the Game Dash. Try again.' : 'Could not unpin the Game Dash. Try again.');

            return null;
        }
    };

    return { pinned, ready, toggle };
};

/**
 * Whether the inline Game Dash widget renders, and why: newbies see it by
 * default, everyone else once they pin it from the bell dialog.
 */
export const useGameDashVisibility = () => {
    const { auth } = useAuth();
    const { pinned } = useGameDashPin();

    const handle = computed(() => auth.user?.screen_name);
    const { data: brain, isSuccess } = useBrain(handle, OVERALL_PARAMS);

    // Only a loaded brain decides: signed out or still loading is not a
    // newbie, so the widget never flashes in and back out.
    const newbie = computed(() => {
        if (!auth.authenticated || !isSuccess.value) return false;

        const level = brain.value?.brain_level;

        // A brand-new player may have no level yet.
        return level == null || Number(level) < NEWBIE_LEVEL_LIMIT;
    });

    const reason = computed(() => {
        if (newbie.value) return WIDGET_REASON.NEWBIE;
        if (pinned.value && auth.authenticated) return WIDGET_REASON.PINNED;

        return null;
    });

    const visible = computed(() => reason.value !== null);

    return { visible, reason };
};
